import {Component, EventEmitter, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActionsRecorderService} from "./umbrella-common/services/actions-recorder.service";
import {environment} from "../environments/environment";
import {COOKIES_ACCEPTED, getCookie} from "./umbrella-common/common-functions";
import {AuthenticationService} from "./authentication/authentication.service";
import {NavigationService} from "./navigation.service";
import {ActivatedRoute, ActivatedRouteSnapshot, GuardsCheckEnd, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {debounceTime, filter, first, map, tap} from "rxjs/operators";
import {BehaviorSubject, combineLatest, Observable, Subject} from "rxjs";
import {HeaderVisibilityService} from "./umbrella-common/services/header-visibility.service";
import {Actions, ofType} from "@ngrx/effects";
import {LoginActions} from "./store/actions/login.actions";
import {SameUrlNavigationDetector} from "./umbrella-common/services/same-url-navigation-detector.service";
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {LocaleService} from "./umbrella-common/services/locale.service";
import {CookiesSettingsService, CookiesState} from "./client/cookies-settings/cookies-settings.service";
import {MaintenanceInfo} from "./maintenance/banner/maintenance-banner.component";
import {OtherActions} from "./store/actions/other.actions";
import {AppState} from "./store/state/app.state";
import {Store} from "@ngrx/store";
import {SeoService} from "./seo.service";
import {GlobalSpinnerTriggeringService} from "./global-spinner-triggering.service";
import {EndpointsProvider} from "./umbrella-common/endpoints/endpoint-provider.service";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

	private readonly DEFAULT_TAB_NAME: string = 'DEKRA';
	private readonly NON_STANDARD_PAGES= ['review-checklist-document'];
	private readonly FOOTER_VISIBLE_PAGES = ['privacy-policy', 'estimate', 'terms-and-conditions', 'cookies-settings', 'imprint'];
	private readonly COOKIE_BANNER_HIDDEN_PAGES = ['cookies-settings'];

	cookiesAccepted: boolean = false;
	cookiesRejected: boolean = false;
	useStandardLayout: boolean = true;
	rolesForCss$: EventEmitter<string> = new EventEmitter();
	showFooter$: Observable<boolean>;
	hideCookieBanner$: Observable<boolean>;
	maintenanceInfo$: Observable<MaintenanceInfo>;
	showGlobalSpinner$: Observable<boolean>;

	private pageLoading$: Subject<boolean> = new Subject<boolean>();
	private currentScrollTop: number = 0;
	private previousScrollTop: number = 0;
	title: string;
	displayVersion: boolean;


	constructor(private actionRecorder: ActionsRecorderService, private authService: AuthenticationService, private navigationService: NavigationService,
				private router: Router, public headerVisibilityService: HeaderVisibilityService, private actions$: Actions,
				private sameUrlDetectors: SameUrlNavigationDetector, private translateService: TranslateService, private route: ActivatedRoute, private localeService: LocaleService,
				private cookiesService: CookiesSettingsService, private endpoints: EndpointsProvider, private store: Store<AppState>,
				private seo: SeoService, private globalSpinnerTrigger: GlobalSpinnerTriggeringService) {

	}

	ngOnInit(): void {
		this.localeService.initLocale();
		this.actionRecorder.startRecording(environment.logActionsInConsole);
		this.displayVersion = environment.displayVersionInMainFrame;
		document.getElementById('nb-global-spinner').style.display = 'none';

		this.router.events.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.redirectEmployeeToHisCountryUrlIfNeeded(event)
				this.updatePageMetadata(event);
				this.headerVisibilityService.setVisibility(true);
				this.useStandardLayout = !this.NON_STANDARD_PAGES.some(page => event.url.includes(page));
				setTimeout(() => this.pageLoading$.next(false), 200);
			});

		this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(event => {
			this.pageLoading$.next(true)
		});

		this.router.events.pipe(
			filter(event => event instanceof GuardsCheckEnd),
			filter((event: GuardsCheckEnd) => event.shouldActivate),
			map((event: GuardsCheckEnd) => this.getFixedLocaleIfAny(event.state.root)),
			filter(fixedLocale => !!fixedLocale)
		).subscribe(fixedLocale => this.localeService.setDefaultLocale(fixedLocale))

		if (this.authService.isLoggedAnonymousClient() || this.authService.isJwtTokenPresentAndExpired()) {
			this.authService.logout();
		}

		this.resolveUserRolesForCssStyling();
		this.sameUrlDetectors.init();
		this.showFooter$ = this.getShowComponentFor$(this.FOOTER_VISIBLE_PAGES);
		this.hideCookieBanner$ = this.getHideComponentFor$(this.COOKIE_BANNER_HIDDEN_PAGES);

		this.cookiesService.handleCookiesAtAppStart();
		this.cookiesService.allAccepted().subscribe(cookiesState => {
			if (getCookie(COOKIES_ACCEPTED) !== '' && this.currentLang() === 'en-US') {
				this.cookiesAccepted = true;
				this.cookiesRejected = false;
			} else if (cookiesState === CookiesState.ACCEPTED || getCookie(COOKIES_ACCEPTED) === '1') {
				this.cookiesAccepted = true;
				this.cookiesRejected = false;
			} else if (cookiesState === CookiesState.REJECTED) {
				this.cookiesAccepted = false;
				this.cookiesRejected = true;
			}
		});

		this.maintenanceInfo$ = this.endpoints.appSettings.getMaintenanceInfo().pipe(first(),
			tap(maintenanceInfo => !!maintenanceInfo && this.store.dispatch(OtherActions.SET_MAINTENANCE_INFO({maintenanceInfo}))));

		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			this.seo.updateTags();
		});

		this.showGlobalSpinner$ = combineLatest([this.pageLoading$.asObservable(), this.globalSpinnerTrigger.getObservable()]).pipe(
			map(([pageLoading, manualTrigger]) => pageLoading || manualTrigger)
		);
	}

	private getShowComponentFor$(visiblePages: string[]) {
		return this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(event => visiblePages.some(page => (event as NavigationEnd).url.includes(page)) || this.authService.isLoggedUserClient(true))
		);
	}

	private getHideComponentFor$(hiddenPages: string[]) {
		return this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(event => hiddenPages.some(page => (event as NavigationEnd).url.includes(page)))
		);
	}

	private resolveUserRolesForCssStyling() {
		setTimeout(() => this.rolesForCss$.emit(this.authService.getUser() != null ? this.authService.getUser().roles.join(' ').toLowerCase() : ''), 100);
		this.actions$.pipe(ofType(LoginActions.LOG_OUT)).subscribe(() => this.rolesForCss$.emit(''));

		this.actions$.pipe(
			ofType(LoginActions.LOG_IN_SUCCESSFUL),
			debounceTime(1000),
			map(() => {
				return this.authService.getUser().roles.join(' ').toLowerCase();
			})
		).subscribe(roles => {
			this.rolesForCss$.emit(roles);
		});
	}

	ngOnDestroy(): void {
		this.actionRecorder.stopRecording();
	}

	@HostListener('window:unload')
	onBrowserTabClose() {
		if (this.authService.isLoggedAnonymousClient()) {
			this.authService.logout();
		}
	}

	@HostListener('window:scroll')
	onScroll() {
		const scrollTop = window.scrollY;
		this.currentScrollTop = scrollTop;

		if (this.previousScrollTop < this.currentScrollTop && scrollTop > 0.2 * window.outerWidth) {
			this.headerVisibilityService.setVisibility(false);
			// } else if (this.previousScrollTop > this.currentScrollTop && !(scrollTop <= 0.2 * window.outerWidth)) {
		} else if (this.previousScrollTop > this.currentScrollTop) {
			this.headerVisibilityService.setVisibility(true);
		}

		this.previousScrollTop = this.currentScrollTop;
	}

	@HostListener('window:resize')
	onWindowResize() {
		WINDOW_RESIZED$.next(window.outerWidth);
	}

	get smallScreen(): boolean {
		return isIpad || isMobile;
	}

	isMobile(): Observable<boolean> {
		return WINDOW_RESIZED$.asObservable().pipe(map(outerWidth => outerWidth < 767));
	}

	isClientLoggedIn(): boolean {
		return this.authService.isLoggedUserClient(true);
	}

	isDekraEmployeeLoggedIn() {
		return this.authService.isDekraEmployeeLogged();
	}

	isLoggedIn(): boolean {
		return this.authService.isLoggedIn();
	}

	onDekraLogoClicked() {
		if (this.authService.isDekraEmployeeLogged()) {
			this.navigationService.initialNavigate(true);
		} else {
			this.navigationService.navigateToDekraPage();
		}
	}

	currentLang(): string {
		return this.translateService.currentLang;
	}

	private updatePageMetadata(event: NavigationEnd) {
		this.title = this.translateService.instant(this.getTitle(this.router.routerState.root.snapshot, this.DEFAULT_TAB_NAME));
		this.seo.setTitle(this.title);
		this.addOrRemoveMetaTags(event);
	}

	private getTitle(route: ActivatedRouteSnapshot, parentTitle: string): string {
		if (route.firstChild == null) {
			return this.getTitleForRoute(route, parentTitle);
		}

		return this.getTitle(route.firstChild, parentTitle);
	}

	private getTitleForRoute(route: ActivatedRouteSnapshot, parentTitle: string): string {
		return route.data['title'] ? route.data['title'] : parentTitle;
	}

	private addOrRemoveMetaTags(event: NavigationEnd) {
		if (event.url.includes('estimate')) {
			this.seo.addMetadataTags();
		} else {
			this.seo.removeMetadataTags();
		}
	}

	private getFixedLocaleIfAny(route: ActivatedRouteSnapshot): string | null {
		if (route.data['fixedLocale'] != null) {
			return route.data['fixedLocale'];
		}

		return route.firstChild != null ? this.getFixedLocaleIfAny(route.firstChild) : null;
	}

	private redirectEmployeeToHisCountryUrlIfNeeded(event: NavigationEnd) {
		if (this.authService.isDekraEmployeeLogged()) {
			let countryInUrl = location.href.replace(event.urlAfterRedirects, '').split('/').pop();
			let user = this.authService.getUser();

			if (countryInUrl !== user.countryCode.toLowerCase()) {
				let newUrl = location.href.split('/').map(item => item === countryInUrl ? user.countryCode.toLowerCase() : item).join('/');
				window.open(newUrl, '_self')
			}
		}
	}
}

export const isIE: boolean = window.navigator.userAgent.toLowerCase().indexOf("trident") > -1;

export const isMobile: boolean = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
	// tslint:disable-next-line:max-line-length
	|| /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substring(0, 4));

export const isIpad: boolean = /ipad/i.test(navigator.userAgent);

export type DEVICE_TYPE = 'MOBILE' | 'IE' | 'DESKTOP';

export const deviceType: DEVICE_TYPE = isIE ? "IE" : (isMobile ? "MOBILE" : "DESKTOP");

export const WINDOW_RESIZED$: Subject<number> = new BehaviorSubject(window.innerWidth);
